import { useState } from 'react';
import { createPortalSession, type CreatePortalSessionResult } from '@/lib/createPortalSession';

function getPortalErrorMessage(result: Extract<CreatePortalSessionResult, { ok: false }>) {
  if (result.reason === 'not_authenticated') {
    return 'You are not authenticated. Please sign in again.';
  }

  if (result.reason === 'invoke_failed') {
    return 'Failed to open billing portal. Please try again.';
  }

  if (result.reason === 'missing_url') {
    return 'Billing portal URL missing. Please contact support.';
  }

  return 'An unexpected error occurred. Please try again.';
}

export function useCustomerPortal() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openCustomerPortal = async (returnUrl?: string) => {
    setLoading(true);
    setError(null);

    try {
      const portalResult = await createPortalSession(returnUrl);

      if (portalResult.ok) {
        window.location.href = portalResult.url;
        return;
      }

      const portalFailure = portalResult as Extract<CreatePortalSessionResult, { ok: false }>;
      console.error('Customer portal error:', portalFailure.error);
      setError(getPortalErrorMessage(portalFailure));
    } catch (err) {
      console.error('Customer portal invocation error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return {
    error,
    loading,
    openCustomerPortal,
  };
}
